import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";

function Header() {
  const cartState = useContext(CartContext) || {};
  const { cartItems = [] } = cartState;
  const wishlistItems = cartState.wishlistItems || cartState.wishlist || [];
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);

  const cartCount = useMemo(
    () =>
      Array.isArray(cartItems)
        ? cartItems.reduce(
            (total, item) => total + Math.max(1, Number(item.quantity) || 1),
            0
          )
        : 0,
    [cartItems]
  );

  const wishlistCount = Array.isArray(wishlistItems)
    ? wishlistItems.length
    : Number(wishlistItems?.size) || 0;

  const activeCategory = useMemo(
    () => new URLSearchParams(location.search).get("category"),
    [location.search]
  );

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenPanel(null);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (location.pathname === "/shop") {
      setSearchTerm(new URLSearchParams(location.search).get("q") || "");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenPanel(null);
        setIsMenuOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpenPanel(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
    setOpenPanel(null);
    setIsMenuOpen(false);
  };

  const togglePanel = (panel) => {
    setIsMenuOpen(false);
    setOpenPanel((current) => (current === panel ? null : panel));
  };

  const closeHeaderMenus = () => {
    setOpenPanel(null);
    setIsMenuOpen(false);
  };

  return (
    <header className="site-header" ref={headerRef}>
      <div className="site-header__promotion">
        <div className="site-header__promotion-inner">
          <span>{siteConfig.header.promotion.text}</span>
          <Link
            to={siteConfig.header.promotion.href}
            onClick={closeHeaderMenus}
          >
            {siteConfig.header.promotion.cta}
          </Link>
        </div>
      </div>

      <div className="site-header__main">
        <div className="site-header__main-inner">
          <button
            className="site-header__menu-button"
            type="button"
            aria-label={
              isMenuOpen
                ? `${siteConfig.header.menuLabel} schließen`
                : `${siteConfig.header.menuLabel} öffnen`
            }
            aria-expanded={isMenuOpen}
            aria-controls="primary-navigation"
            onClick={() => {
              setOpenPanel(null);
              setIsMenuOpen((isOpen) => !isOpen);
            }}
          >
            {isMenuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
          </button>

          <Link
            className={`site-header__logo ${
              siteConfig.logo.src ? "has-image" : ""
            }`}
            to="/"
            aria-label={siteConfig.logo.ariaLabel}
            onClick={closeHeaderMenus}
          >
            {siteConfig.logo.src ? (
              <img src={siteConfig.logo.src} alt={siteConfig.logo.alt} />
            ) : (
              <span aria-hidden="true">{siteConfig.logo.text}</span>
            )}
          </Link>

          <form
            className="site-header__search"
            role="search"
            onSubmit={handleSearch}
          >
            <label className="site-header__sr-only" htmlFor="site-search">
              {siteConfig.header.searchLabel}
            </label>
            <input
              id="site-search"
              type="search"
              value={searchTerm}
              placeholder={siteConfig.header.searchPlaceholder}
              onChange={(event) => setSearchTerm(event.target.value)}
              autoComplete="off"
            />
            <button type="submit" aria-label={siteConfig.header.searchLabel}>
              <FiSearch aria-hidden="true" />
            </button>
          </form>

          <div className="site-header__actions" aria-label="Kundenbereich">
            <div className="site-header__action-wrap">
              <button
                className="site-header__action"
                type="button"
                aria-expanded={openPanel === "account"}
                aria-controls="account-panel"
                onClick={() => togglePanel("account")}
              >
                <FiUser aria-hidden="true" />
                <span>{siteConfig.header.accountLabel}</span>
              </button>
              {openPanel === "account" && (
                <div
                  className="site-header__popover"
                  id="account-panel"
                  role="region"
                  aria-label={siteConfig.header.accountLabel}
                >
                  <strong>Dein Kundenkonto</strong>
                  <p>Bestellungen, Beratung und persönliche Vorteile im Blick.</p>
                  <Link to="/contact" onClick={closeHeaderMenus}>
                    Zum Kundenservice
                  </Link>
                </div>
              )}
            </div>

            <div className="site-header__action-wrap">
              <button
                className="site-header__action"
                type="button"
                aria-expanded={openPanel === "wishlist"}
                aria-controls="wishlist-panel"
                onClick={() => togglePanel("wishlist")}
              >
                <span className="site-header__icon-wrap">
                  <FiHeart aria-hidden="true" />
                  {wishlistCount > 0 && (
                    <span className="site-header__count" aria-hidden="true">
                      {wishlistCount > 99 ? "99+" : wishlistCount}
                    </span>
                  )}
                </span>
                <span>{siteConfig.header.wishlistLabel}</span>
                <span className="site-header__sr-only">
                  {wishlistCount} gespeicherte Artikel
                </span>
              </button>
              {openPanel === "wishlist" && (
                <div
                  className="site-header__popover"
                  id="wishlist-panel"
                  role="region"
                  aria-label={siteConfig.header.wishlistLabel}
                >
                  <strong>{siteConfig.header.wishlistLabel}</strong>
                  <p>
                    {wishlistCount > 0
                      ? `${wishlistCount} Artikel sind für dich gespeichert.`
                      : "Deine Merkliste ist noch leer."}
                  </p>
                  <Link to="/shop" onClick={closeHeaderMenus}>
                    Kollektion entdecken
                  </Link>
                </div>
              )}
            </div>

            <Link
              className="site-header__action"
              to="/cartpage"
              onClick={closeHeaderMenus}
              aria-label={`${siteConfig.header.cartLabel}, ${cartCount} Artikel`}
            >
              <span className="site-header__icon-wrap">
                <FiShoppingBag aria-hidden="true" />
                {cartCount > 0 && (
                  <span className="site-header__count" aria-hidden="true">
                    {cartCount > 99 ? "99+" : cartCount}
                  </span>
                )}
              </span>
              <span>{siteConfig.header.cartLabel}</span>
            </Link>
          </div>
        </div>
      </div>

      <nav
        className={`site-header__nav ${isMenuOpen ? "is-open" : ""}`}
        id="primary-navigation"
        aria-label="Hauptnavigation"
      >
        <ul>
          {siteConfig.navigation.map((item) => {
            const isActive =
              location.pathname === "/shop" && activeCategory === item.category;

            return (
              <li key={item.category}>
                <Link
                  className={`${isActive ? "is-active" : ""} ${
                    item.accent ? "is-accent" : ""
                  }`}
                  to={item.href}
                  aria-current={isActive ? "page" : undefined}
                  onClick={closeHeaderMenus}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {isMenuOpen && (
        <button
          className="site-header__scrim"
          type="button"
          aria-label="Menü schließen"
          onClick={closeHeaderMenus}
        />
      )}
    </header>
  );
}

export default Header;
